import { capitalize } from '@/utils';
import type { EmployeeData } from '@/app/features/employee/emp-dashboard/emp-dashboard.type';

interface PersonalInfoProps {
  employee?: EmployeeData;
  loading?: boolean;
}

const PersonalInfo = ({ employee, loading }: PersonalInfoProps) => {
  if (loading) {
    return (
      <div className="grid gap-6 grid-cols-1 lg:grid-cols-2">
        <div className="space-y-6">
          {[1, 2, 3].map((i) => (
            <div key={i} className="bg-theme-slate-800 rounded-xl p-6">
              <div className="animate-pulse space-y-4">
                <div className="h-6 bg-theme-slate-700 rounded w-1/3"></div>
                <div className="space-y-3">
                  <div className="h-4 bg-theme-slate-700 rounded w-1/4"></div>
                  <div className="h-5 bg-theme-slate-700 rounded w-3/4"></div>
                  <div className="h-4 bg-theme-slate-700 rounded w-1/4"></div>
                  <div className="h-5 bg-theme-slate-700 rounded w-1/2"></div>
                </div>
              </div>
            </div>
          ))}
        </div>
        <div className="space-y-6">
          {[1, 2, 3].map((i) => (
            <div key={i} className="bg-theme-slate-800 rounded-xl p-6">
              <div className="animate-pulse space-y-4">
                <div className="h-6 bg-theme-slate-700 rounded w-1/3"></div>
                <div className="space-y-3">
                  <div className="h-4 bg-theme-slate-700 rounded w-1/4"></div>
                  <div className="h-5 bg-theme-slate-700 rounded w-3/4"></div>
                  <div className="h-4 bg-theme-slate-700 rounded w-1/4"></div>
                  <div className="h-5 bg-theme-slate-700 rounded w-1/2"></div>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="grid gap-6 grid-cols-1 lg:grid-cols-2">
      <div className="space-y-6">
        <div className="bg-theme-slate-800 rounded-xl p-6 space-y-10">
          <h6 className="text-2xl text-white font-black">
            Contact Information
          </h6>
          <InfoItem label="Phone" value={employee?.phone || '--'} />
          <InfoItem label="Email" value={employee?.email || '--'} />
          <InfoItem
            label="House Address"
            value={employee?.house_address || '--'}
          />
        </div>
        <div className="bg-theme-slate-800 rounded-xl p-6 space-y-10">
          <h6 className="text-2xl text-white font-black">Emergency Contact</h6>
          {employee?.emergency_contacts &&
          Array.isArray(employee.emergency_contacts) &&
          employee.emergency_contacts.length > 0 ? (
            employee.emergency_contacts.map((contact: any, index: number) => (
              <div key={index} className="space-y-6">
                {employee.emergency_contacts.length > 1 && (
                  <h6 className="text-lg text-white font-semibold">
                    Contact {index + 1}
                  </h6>
                )}
                <InfoItem label="Name" value={contact?.name || '--'} />
                <InfoItem
                  label="Relationship"
                  value={
                    capitalize(contact?.relation) ||
                    capitalize(contact?.relationship) ||
                    '--'
                  }
                />
                <InfoItem
                  label="Phone"
                  value={contact?.mobile || contact?.phone || '--'}
                />
                {index < employee.emergency_contacts.length - 1 && (
                  <div className="border-t border-gray-700 pt-6"></div>
                )}
              </div>
            ))
          ) : (
            <>
              <InfoItem label="Name" value="--" />
              <InfoItem label="Relationship" value="--" />
              <InfoItem label="Phone" value="--" />
            </>
          )}
        </div>
        {/* <div className="bg-theme-slate-800 rounded-xl p-6 space-y-10">
          <h6 className="text-2xl text-white font-black">Personal Details</h6>
          <InfoItem
            label="Date of Birth"
            value={
              employee?.date_of_birth
                ? new Date(employee.date_of_birth).toLocaleDateString("en-US", {
                    year: "numeric",
                    month: "long",
                    day: "numeric",
                  })
                : "--"
            }
          />
          <InfoItem
            label="National ID Card Number"
            value={employee?.nid || "--"}
          />
          <InfoItem label="Blood Group" value={employee?.blood_group || "--"} />
        </div> */}
      </div>
      <div className="space-y-6">
        <div className="bg-theme-slate-800 rounded-xl p-6 space-y-10">
          <h6 className="text-2xl text-white font-black">Personal Details</h6>
          <InfoItem
            label="Date of Birth"
            value={
              employee?.date_of_birth
                ? new Date(employee.date_of_birth).toLocaleDateString('en-US', {
                    year: 'numeric',
                    month: 'long',
                    day: 'numeric',
                  })
                : '--'
            }
          />
          <InfoItem
            label="National ID Card Number"
            value={employee?.nid || '--'}
          />
          <InfoItem label="Blood Group" value={employee?.blood_group || '--'} />
        </div>
        {/* <div className="bg-theme-slate-800 rounded-xl p-6 space-y-10">
          <h6 className="text-2xl text-white font-black">Education</h6>
          <InfoItem
            label="Institute"
            value={employee?.educations?.[0]?.institute || '--'}
          />
          <InfoItem
            label="Degree"
            value={employee?.educations?.[0]?.degree || '--'}
          />
          <InfoItem
            label="Graduation Year"
            value={employee?.educations?.[0]?.graduation_year || '--'}
          />
        </div>
        <div className="bg-theme-slate-800 rounded-xl p-6 space-y-10">
          <h6 className="text-2xl text-white font-black">Skills</h6>
          <Flex align="center" gap={12} wrap>
            {employee?.skills && employee.skills.length > 0 ? (
              employee.skills.map((skill: string, index: number) => (
                <span
                  key={index}
                  className="block px-3 py-2 text-base bg-theme-slate-900 rounded-lg"
                >
                  {skill}
                </span>
              ))
            ) : (
              <span className="text-gray-400">--</span>
            )}
          </Flex>
        </div>
        <div className="bg-theme-slate-800 rounded-xl p-6 space-y-10">
          <h6 className="text-2xl text-white font-black">Certifications</h6>
          {employee?.certifications && employee.certifications.length > 0 ? (
            employee.certifications.map((cert: any, index: number) => (
              <Flex key={index} align="center" gap={12} justify="space-between">
                <h6 className="text-xl text-white">{cert.name || '--'}</h6>
                <h6 className="text-base text-white">{cert.year || '--'}</h6>
              </Flex>
            ))
          ) : (
            <div className="text-gray-400">--</div>
          )}
        </div> */}
      </div>
    </div>
  );
};

const InfoItem = ({ label, value }: { label: string; value: string }) => {
  return (
    <div className="spy-[13px]">
      <h6 className="text-base text-white">{label}</h6>
      <h6 className="text-xl text-white">{value}</h6>
    </div>
  );
};

export default PersonalInfo;
